// Network — outbound egress decisions from BRace-sandboxed containers.
// Sources: uc_nft_drop (decision=block) + brace_connect (decision=allow,
// requires brace.syscalls.connect.enable=true in policy).
//
// Two views:
//   - Recent decisions table — newest-first
//   - Unique destinations roll-up — first-seen / decision breakdown per dst_ip
//
// Polls every 8s.
import { useEffect, useMemo, useState } from "react";
import { ArrowDownToLine, Ban, CheckCircle2 } from "lucide-react";
import Card from "@/components/Card";
import Badge from "@/components/Badge";
import PageHeader from "@/components/PageHeader";
import { useFetch } from "@/lib/api/useFetch";
import type { components } from "@/lib/api/gen/schema";

type NetworkEvent = components["schemas"]["NetworkEvent"];

const POLL_MS = 8000;

export default function Network() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = window.setInterval(() => setTick((n) => n + 1), POLL_MS);
    return () => window.clearInterval(t);
  }, []);

  const { data, error, loading } = useFetch<{ items: NetworkEvent[] }>(
    (api) => api.GET("/network-events", { params: { query: { limit: 200 } } }),
    [tick],
  );

  const items = data?.items ?? [];
  const byDest = useMemo(() => rollupByDest(items), [items]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Network"
        subtitle={
          <>Outbound network decisions from sandbox containers — blocked and
          allowed destinations. Auto-refreshing every {POLL_MS / 1000}s.</>
        }
      />

      <Card title="Unique destinations">
        {loading && !data ? (
          <div className="text-sm text-text-secondary">Loading…</div>
        ) : byDest.length === 0 ? (
          <div className="text-sm text-text-muted">
            No outbound traffic observed yet.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase text-text-muted">
                <th className="py-1.5">Destination</th>
                <th>Protocol·Port</th>
                <th className="text-right">Allowed</th>
                <th className="text-right">Blocked</th>
                <th>First Seen</th>
                <th>Last Seen</th>
              </tr>
            </thead>
            <tbody>
              {byDest.map((d) => (
                <tr key={d.key} className="border-b border-border/40">
                  <td className="py-1.5 font-mono text-xs">{d.dst_ip}</td>
                  <td className="font-mono text-xs text-text-muted">
                    {d.protocol}
                    {d.dst_port ? `:${d.dst_port}` : ""}
                  </td>
                  <td className="text-right">
                    {d.allowed > 0 ? (
                      <span className="text-success">{d.allowed}</span>
                    ) : (
                      <span className="text-text-muted">—</span>
                    )}
                  </td>
                  <td className="text-right">
                    {d.blocked > 0 ? (
                      <span className="text-danger">{d.blocked}</span>
                    ) : (
                      <span className="text-text-muted">—</span>
                    )}
                  </td>
                  <td className="text-xs text-text-secondary">
                    {new Date(d.first).toLocaleTimeString()}
                  </td>
                  <td className="text-xs text-text-secondary">
                    {new Date(d.last).toLocaleTimeString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="Recent decisions">
        {error ? (
          <div className="text-sm text-danger">{error}</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-text-muted">
            No network decisions recorded yet.
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {items.slice(0, 50).map((e) => (
              <NetRow key={e.id} e={e} />
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function NetRow({ e }: { e: NetworkEvent }) {
  const Icon = e.decision === "block" ? Ban : CheckCircle2;
  const color = e.decision === "block" ? "text-danger" : "text-success";
  return (
    <li className="flex items-start gap-3 py-2.5">
      <Icon size={16} className={`mt-0.5 flex-none ${color}`} aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <Badge variant={e.decision === "block" ? "danger" : "success"}>
            {e.decision === "block" ? "Blocked" : "Allowed"}
          </Badge>
          <span className="font-mono text-sm">
            {e.dst_ip}
            {e.dst_port ? `:${e.dst_port}` : ""}
            {e.protocol ? `/${e.protocol}` : ""}
          </span>
          <ArrowDownToLine size={12} className="text-text-muted" />
          <span className="font-mono text-xs text-text-muted">
            from {e.src_ip ?? "?"}
          </span>
          <span className="ml-auto whitespace-nowrap text-xs text-text-muted">
            {new Date(e.timestamp).toLocaleTimeString()}
          </span>
        </div>
        {e.component_id && (
          <div className="mt-0.5 text-xs text-text-muted">
            component: <span className="font-mono">{e.component_id}</span>
          </div>
        )}
      </div>
    </li>
  );
}

interface DestRollup {
  key: string;
  dst_ip: string;
  dst_port: number | undefined;
  protocol: string | undefined;
  allowed: number;
  blocked: number;
  first: string;
  last: string;
}

function rollupByDest(items: NetworkEvent[]): DestRollup[] {
  const map = new Map<string, DestRollup>();
  for (const e of items) {
    const key = `${e.dst_ip}|${e.dst_port ?? ""}|${e.protocol ?? ""}`;
    const cur = map.get(key);
    if (!cur) {
      map.set(key, {
        key,
        dst_ip: e.dst_ip,
        dst_port: e.dst_port,
        protocol: e.protocol,
        allowed: e.decision === "allow" ? 1 : 0,
        blocked: e.decision === "block" ? 1 : 0,
        first: e.timestamp,
        last: e.timestamp,
      });
    } else {
      if (e.decision === "allow") cur.allowed++;
      else cur.blocked++;
      if (e.timestamp < cur.first) cur.first = e.timestamp;
      if (e.timestamp > cur.last) cur.last = e.timestamp;
    }
  }
  return [...map.values()].sort((a, b) => b.last.localeCompare(a.last));
}
